import { App } from 'vue'
import _get from 'lodash/get'
import { sha512 } from 'js-sha512'
import { ITrackingProps, MixpanelTrackingFn } from '@src/types/events.types'
import { OverridedMixpanel } from 'mixpanel-browser'
import { ISuperproperties } from '@src/types/superproperties.types'
import _pick from 'lodash/pick'
import _mapValues from 'lodash/mapValues'
import { loadAnalyticsDependencies } from '@src/plugins/analytics/loadAnalyticsDependencies'
import { campaignParams } from './lastTouchUTMParams'
import events from './events'
import trackingDirective from './trackingDirective'
import { trackSignInEvent } from './trackSignInEvent'

let mixpanel: OverridedMixpanel | undefined
let trackingSalt: string | undefined
let trackingReadyResolve: (value?: unknown) => void
const trackingReady = new Promise((resolve) => {
  trackingReadyResolve = resolve
})

const utmParams = campaignParams()

export interface IAnalyticsPluginOptions {
  getTrackingState: () => string;
  debugMode: boolean;
}

interface IAnalyticsPostMessagePayload {
  event: string; props: ITrackingProps;
}
type AnalyticsPostMessageEvent = MessageEvent<{
  trackMixpanelEvent?: IAnalyticsPostMessagePayload;
  trackComponentBasedEvent?: IAnalyticsPostMessagePayload & { component: string },
  trackLegacyEvent?: IAnalyticsPostMessagePayload
}>

function pickUserProfileProperties(superproperties: Partial<ISuperproperties> = {}) {
  const propertiesToPick: (keyof ISuperproperties)[] = [
    'first_seen',
    'network_id',
    'user_tracking_id'
  ]
  return _pick(superproperties, propertiesToPick)
}

const hashSecretProps = (props: Partial<ITrackingProps> = {}): { [key: string]: any } => {
  const secretPropsMap: { [key: string]: string } = {
    tag: 'tag_hash',
    tag_id: 'tag_hash',
    tag_label: 'tag_hash',
    email: 'email_hash',
    topic: 'topic_hash',
    expertise: 'expertise_hash',
    id: 'id_hash',
    route_query_param_query: 'route_query_param_query_hash',
    route_query_param_search: 'route_query_param_search_hash'
  }
  return Object.keys(props).reduce((result: Partial<ITrackingProps>, key: string) => {
    const secretKey = secretPropsMap[key]
    const isPublic = !secretKey
    const anyValue = props[key]
    if (isPublic) {
      return { ...result, [key]: anyValue }
    }

    if (trackingSalt && anyValue) {
      const stringValue = JSON.stringify(anyValue)
      return { ...result, [secretKey]: sha512(`${stringValue}_${trackingSalt}`) }
    }

    return result
  }, {})
}

export async function trackLegacyEvent(event: string, customProps: Record<string, string | number | boolean | undefined | null> = {}): Promise<void> {
  await trackingReady
  const props = hashSecretProps(customProps)

  if (props.entity === 'solution') {
    props.entity = 'answer'
  }

  return new Promise((resolve) => {
    if (mixpanel) {
      mixpanel.track(event, props, {}, () => resolve())
    } else {
      resolve()
    }
  })
}

export default {
  install(app: App, options: IAnalyticsPluginOptions) {
    const trackMixpanelEvent: MixpanelTrackingFn = async <T>(event: string, props: T): Promise<void> => {
      await trackingReady
      return new Promise((resolve) => {
        if (mixpanel) {
          const hashedProps = hashSecretProps(props as Partial<ITrackingProps>)
          const trackedProps = {
            ...hashedProps,
            state: hashedProps.state || options.getTrackingState()
          }
          mixpanel.track(event, trackedProps, {}, () => resolve())
        } else {
          resolve()
        }
      })
    }

    const trackComponentBasedEvent = (event: string, component: string, customProps: Partial<ITrackingProps> = {}): Promise<void> => {
      const combineWithEventProps: (p: Partial<ITrackingProps>) => ITrackingProps = _get((events as any)[component], event)
      if (!combineWithEventProps) {
        throw new Error('Event name is not specified')
      }

      return trackMixpanelEvent(event, combineWithEventProps(customProps))
    }

    window.addEventListener('message', (event: AnalyticsPostMessageEvent) => {
      if (event.origin === window.origin) {
        if (event.data.trackMixpanelEvent) {
          trackMixpanelEvent(event.data.trackMixpanelEvent.event, event.data.trackMixpanelEvent.props)
        }
        if (event.data.trackLegacyEvent) {
          trackLegacyEvent(event.data.trackLegacyEvent.event, event.data.trackLegacyEvent.props)
        }
        if (event.data.trackComponentBasedEvent) {
          trackComponentBasedEvent(event.data.trackComponentBasedEvent.event, event.data.trackComponentBasedEvent.component, event.data.trackComponentBasedEvent.props)
        }
      }
    })

    trackingReady.then(() => {}) // ensure resolve assigned

    // eslint-disable-next-line no-param-reassign
    app.config.globalProperties.$trackEvent = trackComponentBasedEvent
    // eslint-disable-next-line no-param-reassign
    app.config.globalProperties.$trackMixpanelEvent = trackMixpanelEvent
    // eslint-disable-next-line no-param-reassign
    app.config.globalProperties.$trackingReady = trackingReady

    app.directive('track', trackingDirective)

    loadAnalyticsDependencies().then(({
      mergedProps,
      realmSettings
    }) => {
      trackingSalt = realmSettings.user_tracking.tracking_salt
      const mixpanelToken = realmSettings.user_tracking.mixpanel_analytics_token
      if (!(window as any).Cypress) {
        // Sneaky chunk name, will load as "essentials.js" instead of something with Mixpanel
        (import(/* webpackChunkName: "essentials" */'mixpanel-browser')).then((imported) => {
          mixpanel = imported.default
          mixpanel.init(mixpanelToken, {
            api_host: window.MIXPANEL_API_HOST,
            cross_subdomain_cookie: false,
            ignore_dnt: true,
            debug: options.debugMode,
            persistence: 'localStorage'
          })
          mixpanel.identify(mergedProps.user_tracking_id)
          mixpanel.register(mergedProps)

          const allUtmParams = { ...utmParams, ..._mapValues(campaignParams()) }
          mixpanel.people.set({
            ...allUtmParams,
            ...pickUserProfileProperties(mergedProps)
          })
          mixpanel.register(allUtmParams)
          trackingReadyResolve()
        })
        trackSignInEvent(trackMixpanelEvent, realmSettings?.auth.cookie_same_site_none)
      } else {
        trackingReadyResolve()
      }
    })
  }
}
